import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';

import { EmbedContentSection } from '@/components/ui/features/lecture/EmbedContentSection/EmbedContentSection';
import { EmbedLectureHeader } from '@/components/ui/features/lecture/EmbedLectureHeader/EmbedLectureHeader';
import { LecturePageSeo } from '@/components/ui/features/lecture/LecturePageSeo/LecturePageSeo';
import { Layout } from '@/components/ui/shared/layouts/Layout/Layout';
import { accessFrom } from '@/constants/custom-request-headers';
import { AccessFrom } from '@/enums/AccessFrom';
import { queryArticlesByLectureId } from '@/fetchers/articles';
import { queryKeywordsByLectureId } from '@/fetchers/keywords';
import { queryLectureProfilesByLectureId } from '@/fetchers/lectureProfiles';
import { queryLecturesEventRelationships } from '@/fetchers/thinEvents';
import {
  queryLectureSimilarRelationship,
  querySubsequentLecture,
  queryThinLectureWithTagAndEvent,
} from '@/fetchers/thinLectures';
import { getScholarTagsForLectureId } from '@/queries/google-scholar-tags';
import { queryLectureAccessPermission } from '@/queries/lecture-access-permissions';
import { queryUserSession } from '@/queries/sessions';
import { queryTranscriptsByLectureId } from '@/queries/transcripts';
import { ThinLecture } from '@/resources/ThinLecture';
import { SESSION_ME } from '@/utils/constants';
import { generateStore } from '@/utils/generateStore';
import { Hydrate } from '@/utils/swr/Hydrate';
import { SlugIdParam, urlQueryStringValidate } from '@/utils/url-query-string';

const LecturePage: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({
  fallback,
  accessFromLocation,
}) => {
  return (
    <Hydrate fallback={fallback}>
      <LecturePageSeo />
      <Layout accessFrom={accessFromLocation}>
        <EmbedLectureHeader />
        <EmbedContentSection />
      </Layout>
    </Hydrate>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const store = generateStore({ context });

  const { id } = urlQueryStringValidate<{ id: ReturnType<typeof SlugIdParam> }>(context.query, { id: SlugIdParam });

  // temp: https://app.productive.io/1-infinum/tasks/6934694
  if (!id) {
    const idQuery = context.query?.id?.toString();

    if (idQuery && idQuery.match(/^\d+$/)) {
      const lecture = await store.fetchResource(queryThinLectureWithTagAndEvent(idQuery)).catch(() => null);

      if (lecture) {
        return {
          redirect: {
            destination: `/lecture/${lecture.data.linkSlug}`,
            permanent: true,
          },
        };
      }
    }
  }

  const lecture = await store.fetchResource(queryThinLectureWithTagAndEvent(id)).catch(() => null);

  if (!lecture) {
    return {
      notFound: true,
    };
  }

  let nextLectureResponse, similarLecturesResponse;
  const concurrentPromises = [];

  try {
    // handle for users referencing non-existing lectures
    [nextLectureResponse, similarLecturesResponse] = await Promise.all([
      store.fetchResources(querySubsequentLecture(id)),
      store.fetchResourceRelationship(queryLectureSimilarRelationship(id)),
    ]);
  } catch (error) {
    return {
      notFound: true,
    };
  }

  const nextLecture = nextLectureResponse?.data?.[0];
  const similarLecture = similarLecturesResponse?.data?.[0];

  if (nextLecture?.id) {
    concurrentPromises.push(store.fetchResource(queryLectureAccessPermission(nextLecture?.id)));
  }

  if (similarLecture?.id) {
    concurrentPromises.push(store.fetchResource(queryLectureAccessPermission(similarLecture?.id)));
  }

  if (similarLecturesResponse?.data && (similarLecturesResponse?.data as Array<ThinLecture>).length) {
    concurrentPromises.push(
      store.fetchResources(queryLecturesEventRelationships(similarLecturesResponse?.data as Array<ThinLecture>))
    );
  }

  try {
    await store.fetchResource(queryUserSession(SESSION_ME));
  } catch (error) {
    // not logged in
  }

  try {
    await Promise.all([
      store.fetchResource(queryLectureAccessPermission(id)),
      store.fetchResources(queryLectureProfilesByLectureId(id)),
      store.fetchResources(queryKeywordsByLectureId(id)),
      store.fetchResources(queryArticlesByLectureId(id)),
      store.fetchResources(queryTranscriptsByLectureId(id)),
      store.fetchResource(getScholarTagsForLectureId(id)),
      ...concurrentPromises,
    ]);
  } catch (err) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      id,
      fallback: store.fallback,
      accessFromLocation: context.req.headers[accessFrom] || AccessFrom.Core,
    },
  };
};

export default LecturePage;
